import { Diskette, Stack as StackIcon } from '@good/icons';
import { Box, Button, Group, Stack, Divider, Collapse, Text, Paper, Center } from '@good/ui/core';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { CustomViewsModalType } from 'utilities/enum-utils';
import CreateNewActivityRecordModalV2 from '../other/listings/components/CreateNewActivityRecordModalV2';
import { useDisclosure } from '@good/ui/hooks';
import { useHistory } from 'react-router-dom';
import { BookingViewTabs } from './booking-view-tabs';
import { PageContainer } from 'layouts/page-container';
import { ICustomView } from 'interfaces/custom-views-interface';
import CustomViewsModal from 'common-components/custom-views/modals/CustomViewsModal';
import { defaultFilterValue, BookingsViewType, BookingsViewLayoutType } from './types';
import { BookingsViewToolbarHeader } from './bookings-view-toolbar-header';
import { BookingsViewToolbarViewControl } from './bookings-view-toolbar-view-control';
import { BookingsViewToolbarRosterModeControl } from './bookings-view-toolbar-roster-mode-control';
import { BookingsViewToolbarFilters } from './booking-view-toolbar-filters';
import { useInitialiseTabs } from './use-initialise-tabs';
import { AddViewButton } from './add-view-button';
import { AllViewsButton } from './all-views-button';
import { LayoutTypeSegmentedControl } from './layout-type-segmented-control';
import { useBookings } from '../books-view-context';

interface BookingsViewToolbarProps {
  view: BookingsViewType;
  onChangeView: (view: BookingsViewType) => void;
}

export const BookingsViewToolbar = ({ view, onChangeView }: BookingsViewToolbarProps) => {
  const bookingsFilter = useSelector((state: IRootState) => state.bookingsStore.bookingsFilter);
  const [createActivityRecordOpened, { open: openCreateActivityRecord, close: closeCreateActivityRecord }] =
    useDisclosure(false);
  const history = useHistory();
  const [layoutType, setLayoutType] = useState<BookingsViewLayoutType>('regular');
  const [bookingViewOpened, { open: openBookingView, close: closeBookingView }] = useDisclosure(false);
  const [bookingViewType, setBookingViewType] = React.useState<CustomViewsModalType>(CustomViewsModalType.CREATE);
  const bookingListingActiveTab = useSelector((state: IRootState) => state.bookingsStore.bookingListingActiveTab);
  const dispatch = useDispatch<IRootDispatch>();
  const bookingViews = useSelector((state: IRootState) => state.bookingsStore.bookingViews);
  const defaultBookingViews = useSelector((state: IRootState) => state.bookingsStore.defaultBookingViews);
  const onOpenCreateBooking = useBookings((selector) => selector.onOpenCreateBooking);
  const [showBookingViewPopover, setShowBookingViewPopover] = useState(false);
  useInitialiseTabs();

  const onChangeBookingViewType = useCallback((type: CustomViewsModalType) => {
    setBookingViewType(type);
  }, []);

  const onChangeShowBookingViewPopover = useCallback((visible: boolean) => {
    setShowBookingViewPopover(visible);
  }, []);

  const onSaveView = useCallback(
    (tab: ICustomView) => {
      if (tab?.isOwner) {
        openBookingView();
        setBookingViewType(CustomViewsModalType.SAVE_VIEW);
      } else if (tab?.isDefault) {
        openBookingView();
        setBookingViewType(CustomViewsModalType.SAVE_DEFAULT_VIEW);
      } else {
        openBookingView();
        setBookingViewType(CustomViewsModalType.SAVE_AS_NEW_COPY_FROM_OTHERS);
      }
    },
    [openBookingView],
  );

  const onOpenBookingViewModal = useCallback(
    (type: CustomViewsModalType) => {
      setBookingViewType(type);
      setShowBookingViewPopover(false);
      openBookingView();
    },
    [openBookingView],
  );

  const onCloseBookingViewModal = useCallback(() => {
    setBookingViewType(CustomViewsModalType.CREATE);
    closeBookingView();
  }, [closeBookingView]);

  const onChangeLayoutType = useCallback((value: BookingsViewLayoutType) => {
    setLayoutType(value);
  }, []);

  return (
    <>
      <PageContainer pb={0}>
        <BookingsViewToolbarHeader
          openCreateBooking={onOpenCreateBooking}
          openCreateActivityRecord={openCreateActivityRecord}
        />
      </PageContainer>
      <Divider />
      <Box pos='sticky' top={60} style={{ zIndex: 1 }}>
        <PageContainer pt='sm' pb='sm' bg='rgb(251, 251, 251)'>
          <Group gap='xs'>
            <BookingsViewToolbarViewControl view={view} onChangeView={onChangeView} />
            <Divider orientation='vertical' my={4} />
            <LayoutTypeSegmentedControl value={layoutType} onChange={onChangeLayoutType} />
            {view === 'calendar' && (
              <>
                <Divider orientation='vertical' my={4} />
                <BookingsViewToolbarRosterModeControl />
              </>
            )}
            <Divider orientation='vertical' my={4} />
            <Button.Group>
              <Paper
                px='xl'
                withBorder
                bg='gray.0'
                style={{
                  borderRight: 'none',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                <Center h='100%'>
                  <StackIcon />
                  <Text size='sm' fw={600} ml='xs'>
                    Views
                  </Text>
                </Center>
              </Paper>
              {layoutType === 'compact' && (
                <BookingViewTabs
                  onChangeBookingViewType={onChangeBookingViewType}
                  openBookingViewModal={openBookingView}
                  bookingViews={bookingViews}
                  compact
                />
              )}
              <Button
                onClick={() => onSaveView([bookingListingActiveTab])}
                fw={600}
                variant='default'
                leftSection={<Diskette />}
              >
                Save
              </Button>
              <AddViewButton
                openBookingViewModal={onOpenBookingViewModal}
                bookingViews={bookingViews}
                showPopover={showBookingViewPopover}
                onChangePopover={onChangeShowBookingViewPopover}
              />
              <AllViewsButton />
            </Button.Group>
          </Group>
        </PageContainer>
        <Divider />
        <Collapse in={layoutType === 'regular'}>
          <PageContainer pt='sm' pb={0} bg='rgb(253, 253, 253)'>
            <Stack gap='xs'>
              <BookingsViewToolbarFilters view={view} />
              <BookingViewTabs
                onChangeBookingViewType={onChangeBookingViewType}
                openBookingViewModal={openBookingView}
                bookingViews={bookingViews}
              />
            </Stack>
          </PageContainer>
        </Collapse>
        {layoutType === 'regular' && <Divider />}
      </Box>
      {createActivityRecordOpened && (
        <CreateNewActivityRecordModalV2
          isOpen={createActivityRecordOpened}
          onClose={closeCreateActivityRecord}
          history={history}
        />
      )}
      <CustomViewsModal
        isOpen={bookingViewOpened}
        onCloseViewModal={onCloseBookingViewModal}
        onUpdateViewModal={onOpenBookingViewModal}
        type={bookingViewType}
        pageViews={bookingViews}
        pageFilter={bookingsFilter}
        pageListingActiveTab={bookingListingActiveTab}
        setPageFilter={dispatch.bookingsStore.setBookingsFilter}
        doAddView={dispatch.bookingsStore.doAddBookingView}
        doDeleteView={dispatch.bookingsStore.doDeleteBookingView}
        doUpdateViewTab={dispatch.bookingsStore.doUpdateBookingView}
        doDuplicateView={dispatch.bookingsStore.doDuplicateBookingView}
        defaultFilterValue={defaultFilterValue}
        defaultViews={defaultBookingViews}
      />
    </>
  );
};
